import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import colors from 'theme/colors';
import breakpoints from 'theme/breakpoints';
import Text from 'components/_UI/Text';
import Fieldset from 'components/_UI/Fieldset';
import Loader from 'components/_UI/Loader';
import { useForm } from 'react-hook-form';
import fieldValidations from 'helpers/fieldValidations';
import fieldMessages from 'texts/messages/field';
import loginApi from 'api/requests/login';
import { AlertCircle, ArrowLeft, CheckCircle, Lock } from 'lucide-react';

export default function ForgotPasswordNewPassword({ email, code, onSuccess, onBack }) {
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm();

  const password = watch('password');

  async function onSubmit({ password, confirmPassword }) {
    if (password !== confirmPassword) {
      setErrorMessage(fieldMessages.passwordMustBeEqual);
      return;
    }

    setIsLoading(true);
    setErrorMessage('');

    try {
      await loginApi.changePassword({
        code,
        email: email.toLowerCase(),
        password,
      });
      onSuccess();
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Ocorreu um erro. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Container>
      <HeaderArea>
        <BackButton onClick={onBack} aria-label="Voltar">
          <ArrowLeft size={18} color={colors.night} />
        </BackButton>
        <StepIndicator>
          <StepDot />
          <StepDot />
          <StepDot active />
        </StepIndicator>
      </HeaderArea>

      <IconCircle>
        <Lock size={22} color={colors.occasionalPurple} />
      </IconCircle>
      <Title>Nova senha</Title>
      <Subtitle>
        Crie uma nova senha para sua conta.
      </Subtitle>

      {errorMessage && (
        <ErrorBanner>
          <AlertCircle size={16} color={colors.danger} />
          <Text color={colors.danger} name="small" type="bold">
            {errorMessage}
          </Text>
        </ErrorBanner>
      )}

      <FormWrapper onSubmit={handleSubmit(onSubmit)}>
        <FieldGroup>
          <FieldLabel>
            Nova senha<Required>*</Required>
          </FieldLabel>
          <Fieldset
            layout="squired"
            placeholder="Crie uma nova senha"
            inputType="password"
            badgeIconPassword
            badgeIconColorPassword={colors.night}
            badgeIconPositionPassword="right"
            name="password"
            register={register}
            validations={fieldValidations.password()}
          />
          {errors.password?.message && (
            <ErrorText>{errors.password.message}</ErrorText>
          )}
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>
            Confirmar senha<Required>*</Required>
          </FieldLabel>
          <Fieldset
            layout="squired"
            placeholder="Repita sua nova senha"
            inputType="password"
            badgeIconPassword
            badgeIconColorPassword={colors.night}
            badgeIconPositionPassword="right"
            name="confirmPassword"
            register={register}
            validations={{
              ...fieldValidations.password('confirmação de senha'),
              validate: (value) =>
                value === password || fieldMessages.passwordMustBeEqual,
            }}
          />
          {errors.confirmPassword?.message && (
            <ErrorText>{errors.confirmPassword.message}</ErrorText>
          )}
        </FieldGroup>

        {isLoading ? (
          <LoaderWrapper>
            <Loader />
          </LoaderWrapper>
        ) : (
          <SubmitButton type="submit">
            <span>Alterar senha</span>
            <CheckCircle size={18} />
          </SubmitButton>
        )}
      </FormWrapper>
    </Container>
  );
}

const fadeIn = keyframes`
  from { opacity: 0; transform: translateX(12px); }
  to { opacity: 1; transform: translateX(0); }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: ${fadeIn} 0.25s ease-out;
`;

const HeaderArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 16px;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;

  &:hover {
    background: ${colors.neutralDark};
  }
`;

const StepIndicator = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`;

const StepDot = styled.div`
  width: ${({ active }) => (active ? '24px' : '8px')};
  height: 8px;
  border-radius: 4px;
  background: ${({ active }) => (active ? colors.occasionalPurple : colors.neutralDark)};
  transition: all 0.3s ease;
`;

const IconCircle = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #f3eaff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${colors.nightDark};
  margin: 0;
  text-align: center;

  @media screen and (max-width: ${breakpoints.sm}) {
    font-size: 18px;
  }
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: ${colors.night};
  margin-top: 4px;
  margin-bottom: 20px;
  text-align: center;
  line-height: 1.5;
`;

const FormWrapper = styled.form`
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
`;

const FieldGroup = styled.div`
  width: 100%;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: ${colors.nightDark};
  letter-spacing: 0.01em;
  margin-bottom: 6px;
`;

const Required = styled.span`
  color: ${colors.danger};
`;

const ErrorText = styled.p`
  color: ${colors.danger};
  font-size: 12px;
  margin-top: 4px;
`;

const ErrorBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  background: #fef2f2;
  border: 1px solid ${colors.dangerLight};
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 16px;
`;

const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  max-width: 280px;
  margin: 8px auto 0;
  padding: 12px 28px;
  background: ${colors.secondaryPurple};
  color: ${colors.neutralLight};
  border: 1px solid ${colors.secondaryPurple};
  border-radius: 50px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 3px ${colors.nightLight};

  &:hover {
    opacity: 0.9;
    box-shadow: 0 4px 12px rgba(105, 12, 228, 0.25);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 12px 0;
`;
